<template>
  <el-dialog
    :model-value="modelValue"
    :title="$t('Saisie des numéros de série')"
    width="560px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="!loading"
    :destroy-on-close="true"
    :before-close="onBeforeClose"
    @open="resetForm"
  >
    <p class="text-gray-600 mb-6">
      {{ $t("{N} équipements à identifier pour valider l'expédition.").replace("{N}", String(qteAttendue)) }}
    </p>

    <el-alert
      v-if="globalError"
      class="mb-5"
      type="error"
      :closable="false"
      :title="globalError"
    />

    <div class="mb-4" v-for="(sn, index) in serials" :key="'sn-' + index">
      <label class="fw-bold text-gray-800 fs-7 mb-1 d-block">
        {{ $t("Numéro de série") }} {{ index + 1 }}
      </label>
      <el-input
        v-model="serials[index]"
        :placeholder="$t('Numéro de série')"
        :disabled="loading"
        :class="{ 'is-error': snErrors[index] }"
      />
      <div v-if="snErrors[index]" class="text-danger fs-8 mt-1">{{ snErrors[index] }}</div>
    </div>

    <div v-if="showPere" class="mt-8 pt-5 border-top">
      <el-alert
        class="mb-4"
        type="warning"
        :closable="false"
        :title="$t('Plusieurs bons de livraison correspondent. Indiquez le ticket de sortie globale.')"
      />
      <label class="fw-bold text-gray-800 fs-7 mb-1 d-block">{{ $t("Ticket de sortie globale") }}</label>
      <el-input
        v-model="ticSeqCible"
        type="number"
        :placeholder="$t('N° du ticket de sortie globale')"
        :disabled="loading"
        :class="{ 'is-error': pereError }"
      />
      <div v-if="pereError" class="text-danger fs-8 mt-1">{{ pereError }}</div>

      <div v-if="bls.length" class="mt-4">
        <div class="text-muted fs-8 mb-2">{{ $t("Tickets possibles :") }}</div>
        <button
          v-for="bl in bls"
          :key="bl.tic_seq"
          type="button"
          class="btn btn-sm btn-light-primary me-2 mb-2"
          :disabled="loading"
          @click="ticSeqCible = String(bl.tic_seq)"
        >
          #{{ bl.tic_seq }} <span v-if="bl.tic_c_label">— {{ bl.tic_c_label }}</span>
        </button>
      </div>
    </div>

    <template #footer>
      <button class="btn btn-light me-3" type="button" :disabled="loading" @click="cancel">
        {{ $t("Annuler") }}
      </button>
      <button class="btn btn-primary" type="button" :disabled="loading || !canSubmit" @click="confirm">
        {{ $t("Confirmer") }}
      </button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="loading"
    width="360px"
    align-center
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    append-to-body
    modal-class="sn-exped-loading-mask"
  >
    <div class="text-center py-6">
      <div class="lds-ring mx-auto mb-4" style="position: relative; width: 64px; height: 64px;">
        <div></div><div></div><div></div><div></div>
      </div>
      <div class="fw-bolder text-gray-800">{{ $t("En cours…") }}</div>
      <div class="text-muted fs-7 mt-2">{{ $t("Traitement en cours, veuillez patienter.") }}</div>
    </div>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs, computed, watch } from "vue";
import { ElMessageBox } from "element-plus";
import { useI18n } from "vue-i18n";
import mAxiosApi from "@/api";

interface SnMatch {
  tic_seq?: number;
}

interface SnResultRow {
  index?: number;
  serial?: string;
  code?: string;
  message?: string;
  matches?: SnMatch[];
}

interface SnBlHint {
  tic_seq?: number;
  tic_c_label?: string;
  bon_seq?: number;
}

interface PocPayload {
  tic_seq: number;
  serials: string[];
  tic_seq_cible?: number;
}

interface PocBody {
  code?: string;
  message?: string;
  results?: SnResultRow[];
  data?: {
    code?: string;
    message?: string;
    results?: SnResultRow[];
    bls?: SnBlHint[];
  };
}

const CIBLE_CODES = [
  "cible_not_found",
  "cible_same_ticket",
  "cible_company_mismatch",
  "cible_no_bl",
  "invalid_cible",
];

const TICKET_CODES = [
  "ticket_not_found",
  "no_expected_qty",
  "invalid_ticket",
  "qte_too_large",
];

const BODY_CODES = [
  "serials_empty",
  "serials_duplicate",
  "serials_count_mismatch",
  "invalid_payload",
];

const MIXED_CODES = [
  "mixed_toc",
  "mixed_ref",
  "mixed_bon",
  "liv_not_on_pere",
  "loc_ref_mismatch",
  "sn_qty_exceeds_liv",
  "preview_unresolved",
];

export default defineComponent({
  name: "ModalSnExped",
  props: {
    modelValue: { type: Boolean, default: false },
    ticSeq: { type: Number, default: 0 },
    qteAttendue: { type: Number, default: 0 },
  },
  emits: ["update:modelValue", "success", "cancel"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const state = reactive({
      serials: [] as string[],
      snErrors: {} as Record<number, string>,
      globalError: "",
      pereError: "",
      showPere: false,
      ticSeqCible: "",
      bls: [] as SnBlHint[],
      loading: false,
      closedBySuccess: false,
    });

    const canSubmit = computed(() => {
      if (!state.serials.length) return false;
      if (state.serials.some((sn) => !String(sn || "").length)) return false;
      if (state.showPere && !String(state.ticSeqCible || "").length) return false;
      return true;
    });

    const resetForm = () => {
      const n = Math.max(0, parseInt(String(props.qteAttendue), 10) || 0);
      state.serials = Array.from({ length: n }, () => "");
      state.snErrors = {};
      state.globalError = "";
      state.pereError = "";
      state.showPere = false;
      state.ticSeqCible = "";
      state.bls = [];
      state.loading = false;
      state.closedBySuccess = false;
    };

    watch(
      () => props.modelValue,
      (open) => {
        if (open) resetForm();
      }
    );

    const parsePoc = (body: PocBody | null | undefined) => {
      if (!body || typeof body !== "object") {
        return { code: "write_failed", message: t("Erreur de communication, vérifiez votre connexion."), results: [] as SnResultRow[], bls: [] as SnBlHint[] };
      }
      return {
        code: body.code || body.data?.code || "",
        message: body.message || body.data?.message || "",
        results: body.data?.results || body.results || [],
        bls: body.data?.bls || [],
      };
    };

    const snErrorLabel = (row: SnResultRow) => {
      const code = row?.code;
      if (code === "sn_already_on_ticket") {
        const found = row.matches && row.matches[0] && row.matches[0].tic_seq;
        return found
          ? t("Déjà dans un ticket pour le projet en cours") + " (#" + found + ")"
          : t("Déjà dans un ticket pour le projet en cours");
      }
      if (code === "sn_not_unique") {
        return t("Ce numéro de série n'est pas unique sur le bon source");
      }
      if (code === "sn_not_in_outbound") {
        return t("Numéro de série introuvable sur le bon de sortie");
      }
      return row?.message || code || t("Numéro de série invalide");
    };

    const applySerialErrors = (results: SnResultRow[]) => {
      state.snErrors = {};
      (results || []).forEach((row) => {
        if (!row || row.code === "ok" || row.code === "multiple_bl") return;
        const idx = typeof row.index === "number" ? row.index : -1;
        if (idx >= 0) state.snErrors[idx] = snErrorLabel(row);
      });
    };

    const send = async () => {
      if (state.loading || !canSubmit.value) return;
      state.loading = true;
      state.globalError = "";
      state.pereError = "";
      state.snErrors = {};

      const payload: PocPayload = {
        tic_seq: props.ticSeq,
        serials: state.serials.map((sn) => String(sn)),
      };
      if (state.showPere) {
        const cible = parseInt(String(state.ticSeqCible), 10);
        if (cible > 0) payload.tic_seq_cible = cible;
      }

      try {
        const response = await mAxiosApi.post("/proxyBasculeSnExped", payload);
        const parsed = parsePoc(response && (response.data as PocBody));
        const code = parsed.code;

        if (code === "accepted") {
          state.loading = false;
          state.closedBySuccess = true;
          emit("update:modelValue", false);
          emit("success");
          return;
        }

        if (code === "multiple_bl") {
          state.showPere = true;
          state.bls = parsed.bls || [];
          state.globalError = "";
          state.loading = false;
          return;
        }

        if (code === "serials_invalid") {
          applySerialErrors(parsed.results);
          if (!Object.keys(state.snErrors).length) {
            state.globalError = parsed.message || t("Un ou plusieurs numéros de série sont invalides.");
          }
          state.loading = false;
          return;
        }

        if (CIBLE_CODES.indexOf(code) >= 0) {
          state.showPere = true;
          state.pereError = parsed.message || t("Ticket de sortie globale invalide.");
          state.loading = false;
          return;
        }

        if (code === "saisi_already_has_bl") {
          state.globalError = parsed.message || t("Ce ticket a déjà un bon de sortie (un site = une attribution).");
          state.loading = false;
          return;
        }

        if (TICKET_CODES.indexOf(code) >= 0) {
          state.globalError = parsed.message || t("Ticket introuvable ou sans service expédition.");
          state.loading = false;
          return;
        }

        if (BODY_CODES.indexOf(code) >= 0) {
          state.globalError = parsed.message || t("Les numéros de série saisis sont incomplets.");
          state.loading = false;
          return;
        }

        if (MIXED_CODES.indexOf(code) >= 0) {
          state.globalError = parsed.message || t("Les numéros de série ne correspondent pas au même lot.");
          state.loading = false;
          return;
        }

        if (code === "write_failed") {
          state.globalError = parsed.message || t("Erreur serveur lors de l'attribution, veuillez réessayer.");
          state.loading = false;
          return;
        }

        state.globalError = parsed.message || t("Erreur serveur lors de l'attribution, veuillez réessayer.");
        state.loading = false;
      } catch {
        state.globalError = t("Erreur de communication, vérifiez votre connexion.");
        state.loading = false;
      }
    };

    const confirm = () => {
      if (state.loading || !canSubmit.value) return;
      ElMessageBox.confirm(
        t("Êtes-vous sûr de vouloir attribuer ces SN à ce ticket ?"),
        t("Confirmation"),
        { confirmButtonText: t("Oui"), cancelButtonText: t("Non, annuler"), type: "warning" }
      )
        .then(() => {
          send();
        })
        .catch(() => {
          return;
        });
    };

    const cancel = () => {
      if (state.loading) return;
      emit("update:modelValue", false);
      emit("cancel");
    };

    const onBeforeClose = (done: () => void) => {
      if (state.loading) return;
      if (!state.closedBySuccess) {
        emit("cancel");
      }
      done();
    };

    return {
      ...toRefs(state),
      canSubmit,
      resetForm,
      confirm,
      cancel,
      onBeforeClose,
    };
  },
});
</script>

<style>
.sn-exped-loading-mask {
  z-index: 5000 !important;
}
.sn-exped-loading-mask + .el-overlay-dialog,
.el-overlay.is-message-box {
  z-index: 5100 !important;
}
</style>
